import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { Text } from "../../components/Text";
import { ChatMessageTimestamp } from "../ChatMessageTimestamp";
import type { FileChatMessage, ReplyContext } from "../types";
import { formatMessageBubbleTime } from "../utils/formatMessageTime";
import { ChatFileAttachment } from "./ChatFileAttachment";
import { MessageOverflowMenu } from "./MessageOverflowMenu";
import { ReplyIconButton } from "./ReplyIconButton";

interface ChatFileMessageProps {
  message: FileChatMessage;
  isIncoming: boolean;
  /**
   * IANA time zone of the workplace this chat belongs to (e.g. "America/New_York").
   * When provided, the per-message timestamp renders in this time zone so it
   * matches the channel list. When omitted, falls back to the viewer's device time zone.
   */
  facilityTimeZone?: string;
  onReply?: (context: ReplyContext) => void;
  onMarkAsUnread?: (messageId: number) => void;
  sx?: SxProps<Theme>;
}

function getMessageBubbleStyles(theme: Theme, isIncoming: boolean) {
  return {
    display: "inline-flex",
    flexDirection: "column" as const,
    gap: 1,
    paddingX: 4,
    paddingY: 3,
    borderRadius: theme.borderRadius?.xLarge,
    backgroundColor: isIncoming
      ? (theme.palette.background.chat?.incoming ?? theme.palette.background.muted)
      : (theme.palette.background.chat?.outgoing ?? theme.palette.primary.main),
    maxWidth: "80%",
    overflowWrap: "break-word" as const,
    wordBreak: "break-word" as const,
  };
}

/**
 * Renders a file attachment message with optional caption
 */
export function ChatFileMessage(props: ChatFileMessageProps) {
  const { message, isIncoming, facilityTimeZone, onReply, onMarkAsUnread, sx } = props;

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: isIncoming ? "flex-start" : "flex-end",
          gap: 1,
        },
        sx
      )}
    >
      <ChatMessageTimestamp
        senderName={message.senderName ?? "Unknown"}
        timestamp={formatMessageBubbleTime(message.createdAt, facilityTimeZone)}
      />
      <Box sx={(theme: Theme) => getMessageBubbleStyles(theme, isIncoming)}>
        <ChatFileAttachment
          fileName={message.fileName}
          fileSize={message.fileSize}
          fileType={message.fileType}
          fileUrl={message.fileUrl}
          thumbnailUrl={message.thumbnailUrl}
        />
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          {message.message && (
            <Text
              variant="body2"
              sx={(theme) => ({
                color: isIncoming
                  ? theme.palette.base?.strong
                  : theme.palette.base?.inverted?.strong,
              })}
            >
              {message.message}
            </Text>
          )}
          {onReply && (
            <ReplyIconButton
              isIncoming={isIncoming}
              sx={{ marginLeft: message.message ? 0 : "auto" }}
              onClick={() => {
                onReply({
                  messageId: message.messageId,
                  message: message.message ?? message.fileName,
                  senderName: message.senderName ?? "Unknown",
                });
              }}
            />
          )}
          {isIncoming && onMarkAsUnread && (
            <MessageOverflowMenu
              isIncoming={isIncoming}
              sx={{ marginLeft: onReply ? 0 : "auto" }}
              onMarkAsUnread={() => {
                onMarkAsUnread(message.messageId);
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
